import { useContext, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CurrentUserContext } from '../../../assets/contexts/currentUserContext';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { useTelegram } from '../../../assets/hooks/useTelegram';
import { PROFILE_LANGUAGES } from '../../../assets/constants/profileData';
import {
  HelpIcon,
  LangIcon,
  NotificationIcon,
  RightArrowIcon,
  ShieldIcon,
  TabProfileIcon,
} from '../../../assets/icons/icons';
import { triggerImpactHaptic } from '../../../assets/utils/hapticFeedback';
import { formatConfirmedPhone } from '../../../assets/utils/phone';
import TelegramUserAvatar from '../../shared/TelegramUserAvatar/TelegramUserAvatar';
import { ProfileLanguageSheet } from '../ProfileLanguage/ProfileLanguage';
import './Profile.css';

function getCurrentUserDisplayName(currentUser, fallback = 'User') {
  const personalData = currentUser?.personal_data || {};
  const candidates = [
    personalData.full_name,
    personalData.fullName,
    [personalData.first_name, personalData.last_name].filter(Boolean).join(' '),
    currentUser?.full_name,
    currentUser?.fullName,
    currentUser?.name,
    fallback,
    currentUser?.email,
    formatConfirmedPhone(currentUser?.phone || currentUser?.phoneNumber),
    currentUser?._id ? `User ${String(currentUser._id).slice(-6)}` : '',
  ];

  return candidates.find((value) => typeof value === 'string' && value.trim()) || fallback;
}

function getTelegramDisplayName(user) {
  if (!user) {
    return '';
  }

  if (user.username) {
    return `@${user.username}`;
  }

  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');

  if (fullName) {
    return fullName;
  }

  return user.id ? String(user.id) : '';
}

function isSameIdentityValue(firstValue, secondValue) {
  const first = String(firstValue || '').trim();
  const second = String(secondValue || '').trim();

  if (!first || !second) {
    return false;
  }

  const firstDigits = first.replace(/\D/g, '');
  const secondDigits = second.replace(/\D/g, '');

  if (firstDigits.length >= 7 && secondDigits.length >= 7) {
    return firstDigits === secondDigits;
  }

  return first.toLocaleLowerCase() === second.toLocaleLowerCase();
}

function ProfileMenuRow({ description, Icon, label, onClick }) {
  const handleClick = () => {
    triggerImpactHaptic('light');
    onClick?.();
  };

  return (
    <button type="button" className="profile__menu-row" onClick={handleClick}>
      <span className="profile__menu-icon" aria-hidden="true">
        <Icon className="profile__menu-icon-svg" />
      </span>
      <span className="profile__menu-copy">
        <span className="profile__menu-label">{label}</span>
        {description ? <span className="profile__menu-description">{description}</span> : null}
      </span>
      <RightArrowIcon className="profile__menu-chevron" />
    </button>
  );
}

function ProfileMenuGroup({ children, label }) {
  return (
    <section className="profile__menu-section">
      <h2 className="profile__section-label">{label}</h2>
      <div className="profile__menu-group">{children}</div>
    </section>
  );
}

function Profile() {
  const navigate = useNavigate();
  const { user } = useTelegram();
  const { currentUser } = useContext(CurrentUserContext);
  const { language, profile } = useContext(TranslationContext);
  const [isLanguageSheetOpen, setIsLanguageSheetOpen] = useState(false);
  const telegramFullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ');
  const displayName = getCurrentUserDisplayName(currentUser, telegramFullName)
    || profile.userFallback;
  const phone = formatConfirmedPhone(currentUser?.phone || currentUser?.phoneNumber);
  const telegramName = getTelegramDisplayName(user);
  const identityPhone = isSameIdentityValue(phone, displayName) ? '' : phone;
  const identityTelegramName = isSameIdentityValue(telegramName, displayName)
    ? ''
    : telegramName;
  const languageId = currentUser?.personal_data?.language || language;
  const languageLabel = PROFILE_LANGUAGES.find(({ id }) => id === languageId)?.label;

  return (
    <section className="profile">
      <div className="profile__content">
        <header className="profile__header">
          <h1 className="profile__title">{profile.title}</h1>
        </header>

        <div className="profile__identity">
          <TelegramUserAvatar className="profile__avatar" />
          <div className="profile__identity-text">
            <span className="profile__name">{displayName}</span>
            {(identityPhone || identityTelegramName) && (
              <span className="profile__identity-meta">
                {identityPhone ? <span>{identityPhone}</span> : null}
                {identityPhone && identityTelegramName ? (
                  <span className="profile__identity-dot" />
                ) : null}
                {identityTelegramName ? <span>{identityTelegramName}</span> : null}
              </span>
            )}
          </div>
        </div>

        <ProfileMenuGroup label={profile.accountSection}>
          <ProfileMenuRow
            Icon={TabProfileIcon}
            label={profile.personalInformation}
            onClick={() => navigate('/profile/personal-information')}
          />
          <div className="profile__menu-divider" />
          <ProfileMenuRow
            Icon={ShieldIcon}
            label={profile.security}
            onClick={() => navigate('/profile/security')}
          />
          <div className="profile__menu-divider" />
          <ProfileMenuRow
            Icon={NotificationIcon}
            label={profile.notifications}
            onClick={() => navigate('/notifications', {
              state: {
                activeTab: 'profile',
                backPath: '/profile',
                fromProfile: true,
              },
            })}
          />
        </ProfileMenuGroup>

        <ProfileMenuGroup label={profile.preferencesSection}>
          <ProfileMenuRow
            Icon={LangIcon}
            label={profile.language}
            description={languageLabel}
            onClick={() => setIsLanguageSheetOpen(true)}
          />
          <div className="profile__menu-divider" />
          <ProfileMenuRow
            Icon={HelpIcon}
            label={profile.helpSupport}
            onClick={() => navigate('/profile/help')}
          />
        </ProfileMenuGroup>

        <AnimatePresence>
          {isLanguageSheetOpen && (
            <ProfileLanguageSheet onClose={() => setIsLanguageSheetOpen(false)} />
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

export default Profile;
